<template lang="pug">
  .b-node__block(
    v-if="!editingCurrent"
    @click.stop="showInput"
  )
    slot
    .b-node__text {{item.text || emptyText}}
    .-flex-divider
  .b-node__block(
    v-else
    ref="block"
  )
    textarea-autosize.b-node__input(
      ref="input"
      v-model="text"
      :max-height="400"
      @input.native="onInput"
      :maxlength="textMax"
      @blur.native="onBlur"
      @keydown.13.native.exact.prevent
      @keyup.13.native.exact="_updateNode"
      rows="1"
      :name="`textarea-${item.id}`"
    )
    .b-node__input-counter(v-if="isSms") {{countSms}}
    tippy(
      v-if="isAutompleted && tippyAllowed"
      :to="`textarea-${item.id}`"
      interactive
      placement="bottom"
      theme="light"
      @shown="tippyOpened = true"
      @hidden="onHideTippy"
      :distance="0"
    )
      .b-autocomplete(
        :style="{'--scale': layoutScale}"
      )
        .b-autocomplete__option(
          v-for="(option, index) in AUTOCOMPLETE_OPTIONS"
          :key="index"
          @click.exact.prevent.stop="setAutocompleteOption(option.value)"
        ) {{option.label}}
</template>

<script>
import { debounce } from 'throttle-debounce';
import { mapActions } from 'vuex';
import { mapFields } from 'vuex-map-fields';
import {
  ELEMENTS_TYPES,
  AUTOCOMPLETE_ELEMENTS_TYPES,
  AUTOCOMPLETE_OPTIONS,
} from '@/api/elements';

const MESSAGES_TOTAL_MAX = 4;
const MESSAGE_MAX_LAT = 160;
const MESSAGE_MAX_CYR = 70;

export default {
  name: 'NodeEditText',
  props: {
    item: Object,
    hasConnection: Boolean,
    isDefault: Boolean,
  },
  data() {
    return {
      text: null,
      AUTOCOMPLETE_OPTIONS,
      tippyAllowed: false,
      tippyOpened: false,
    };
  },
  created() {
    this.text = this.item.text;
  },
  async mounted() {
    await this.$nextTick();
    this.initEventListener();
  },
  computed: {
    ...mapFields('nodes', [
      'editingNodeText',
      'focusId',
      'focusIdInput',
    ]),
    ...mapFields('scale', [
      'layoutScale',
    ]),
    emptyText() {
      return this.isDefault ? 'Default' : 'Empty';
    },
    editingCurrent() {
      return this.editingNodeText === this.item.id;
    },
    textTrimmed() {
      const text = this.text || '';
      return text.toLocaleLowerCase();
    },
    textContainsCyrillic() {
      return /(?=.*[а-я])/.test(this.textTrimmed);
    },
    oneMessageLength() {
      return this.textContainsCyrillic ? MESSAGE_MAX_CYR : MESSAGE_MAX_LAT;
    },
    textMax() {
      return MESSAGES_TOTAL_MAX * this.oneMessageLength;
    },
    isSms() {
      return this.item.type === ELEMENTS_TYPES.SEND_SMS;
    },
    countSms() {
      if (!this.isSms) {
        return false;
      }

      const length = this.textTrimmed.length;

      const messagesAmount = length > 0
        ? Math.ceil(length / this.oneMessageLength)
        : 0;

      return `${length} / ${this.textMax}, ${messagesAmount} sms`;
    },
    isAutompleted() {
      return AUTOCOMPLETE_ELEMENTS_TYPES.includes(this.item.type);
    },
  },
  methods: {
    ...mapActions('nodes', [
      'updateNode',
    ]),
    ...mapActions('visual', [
      'runVisuals',
    ]),
    _updateNode() {
      this.updateNode({
        node: {
          ...this.item,
          text: this.text,
        },
      });
      this.editingNodeText = null;
    },
    showInput(event) {
      if (this.isDefault) {
        return;
      }

      if (this.editingCurrent) {
        event.preventDefault();
        return;
      }
      this.text = this.item.text;
      this.editingNodeText = this.item.id;
    },
    onBlur() {
      if (this.tippyOpened) {
        return;
      }

      this._updateNode();
    },
    onInput: debounce(0, async function (event) {
      const newValue = event && event.target && event.target.value;

      if (newValue && this.item.type === ELEMENTS_TYPES.SAY_VARIABLE) {
        this.text = newValue
          .toLocaleLowerCase()
          .replace(/[^a-z0-9_]/g, '');
      }

      if (newValue && this.item.type === ELEMENTS_TYPES.CONNECT_OPERATOR) {
        this.text = newValue
          .replace(/[^0-9]/g, '')
          .substring(0, 3);
      }

      await this.$nextTick();
      this.delayedRunVisuals();
    }),
    delayedRunVisuals: debounce(200, async function () {
      await this.runVisuals();
    }),
    reset() {
      this.text = this.item.text || null;
      this.editingNodeText = null;
      this.focusId = null;
      this.focusIdInput = null;
    },
    eventPressEsc(event) {
      if (event.keyCode === 27) {
        this.reset();
      }
    },
    eventClickOutside(event) {
      const el = this.$refs.block;
      if (
        !!el
        && event.target !== el
        && !el.contains(event.target)
      ) {
        console.log(event.target);
        event.stopPropagation();
        this.reset();
      }
    },
    initEventListener() {
      if (this.editingCurrent) {
        document.addEventListener('keydown', this.eventPressEsc);
        document.addEventListener('click', this.eventClickOutside);
      } else {
        document.removeEventListener('keydown', this.eventPressEsc);
        document.removeEventListener('click', this.eventClickOutside);
      }
    },
    setAutocompleteOption(value) {
      this.text = value;
      this.tippyAllowed = false;
    },
    onHideTippy() {
      this.tippyOpened = false;
      this._updateNode();
    },
  },
  watch: {
    async editingCurrent(value) {
      await this.$nextTick();
      this.initEventListener();

      if (!!value && this.$refs.input) {
        this.focusId = null;
        this.$refs.input.$el.focus();
        this.tippyAllowed = true;
      }
    },
    async focusId(value) {
      if (value === this.item.id) {
        document.addEventListener('click', this.eventClickOutside);
      }
    },
    async focusIdInput(value) {
      if (value === this.item.id) {
        await this.$nextTick();
        this.showInput();
      }
    },
  },
};
</script>
